interface ViewProps {
    selectedDate: Date;
    appointments: any[];
    clientsById: Map<string, any>;
    barbersById: Map<string, any>;
    onAppointmentClick?: (appointment: any) => void;
    onTimeSlotClick?: (date: Date, hour: number) => void;
    getBarberColors?: (barberId: string, status: string) => string;
}

export default function DayView({
    selectedDate,
    appointments,
    clientsById,
    barbersById,
    onAppointmentClick,
    onTimeSlotClick,
    getBarberColors,
}: ViewProps) {
    const hours = Array.from({ length: 14 }, (_, i) => i + 8);

    const getApptsForHour = (hour: number) => {
        const dayStart = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            hour,
        ).getTime();
        const dayEnd = dayStart + 60 * 60 * 1000;
        return appointments.filter(
            (a) => a.startTime >= dayStart && a.startTime < dayEnd,
        );
    };

    return (
        <div>
            <div className="px-6 py-2 border-b border-gray-200 dark:border-gray-700"></div>
            <div className="max-h-[600px] overflow-y-auto">
                {hours.map((hour) => {
                    const hourAppts = getApptsForHour(hour);
                    return (
                        <div
                            key={hour}
                            className="flex border-b border-gray-100 dark:border-gray-700"
                        >
                            <div className="w-20 p-4 text-sm text-gray-500 dark:text-gray-400 text-right border-r border-gray-100 dark:border-gray-700">
                                {hour}:00
                            </div>
                            <div
                                className="flex-1 p-4 min-h-[80px] cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-colors relative group"
                                onClick={() =>
                                    onTimeSlotClick?.(selectedDate, hour)
                                }
                            >
                                {/* Plus icon for empty slots */}
                                {hourAppts.length === 0 && (
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg shadow-lg border border-emerald-200 dark:border-emerald-700">
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                                />
                                            </svg>
                                            <span className="text-sm font-medium">
                                                Agendar
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {hourAppts.length === 0 ? (
                                    <div className="text-gray-400 dark:text-gray-500 text-sm italic">
                                        Sem agendamentos
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {hourAppts.map((appt, i) => {
                                            const client = clientsById.get(
                                                String(appt.clientId),
                                            );
                                            const colorClass = getBarberColors
                                                ? getBarberColors(
                                                      String(appt.barberId),
                                                      appt.status,
                                                  )
                                                : "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700";
                                            const statusText = {
                                                scheduled: "Agendado",
                                                completed: "Concluído",
                                                canceled: "Cancelado",
                                            } as const;
                                            return (
                                                <div
                                                    key={i}
                                                    className={`p-4 rounded-xl border transition-all duration-300 relative backdrop-blur-sm cursor-pointer ${colorClass}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onAppointmentClick?.(
                                                            appt,
                                                        );
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="font-semibold text-gray-900 dark:text-white text-lg">
                                                            {client?.name ||
                                                                "Cliente"}
                                                        </h4>
                                                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-white/60 dark:bg-gray-800/60 px-2 py-1 rounded-full">
                                                            {statusText[
                                                                appt.status as keyof typeof statusText
                                                            ] || "Agendado"}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                                                        <span className="flex items-center gap-1">
                                                            ⏰{" "}
                                                            {new Date(
                                                                appt.startTime,
                                                            ).toLocaleTimeString(
                                                                "pt-BR",
                                                                {
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                },
                                                            )}{" "}
                                                            -{" "}
                                                            {new Date(
                                                                appt.endTime,
                                                            ).toLocaleTimeString(
                                                                "pt-BR",
                                                                {
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                },
                                                            )}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            ✂️ {appt.service}
                                                        </span>
                                                        {(() => {
                                                            const barber =
                                                                barbersById.get(
                                                                    String(
                                                                        appt.barberId,
                                                                    ),
                                                                );
                                                            return barber ? (
                                                                <span className="flex items-center gap-1">
                                                                    👤{" "}
                                                                    {
                                                                        barber.name
                                                                    }
                                                                </span>
                                                            ) : null;
                                                        })()}
                                                        {client?.phone && (
                                                            <span className="flex items-center gap-1">
                                                                📞{" "}
                                                                {client.phone}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
